/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Fade,
    Flex,
    Stack,
    Text,
    Button,
    VStack,
    HStack,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AddIcon, MinusIcon} from '@chakra-ui/icons'

// Project Components
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Import your custom styles - adjust path as needed
import {
    calloutBoxStyle,
    containerStyle,
    errorMessageStyle,
    flexStyle,
    hStackStyle,
    iconStyle,
    imageStyle,
    inputStyle,
    minusButtonStyle,
    plusButtonStyle,
    priceBaseStyle,
    priceStyle,
    productNameStyle,
    shipToHomeStyle,
    surchargeStyle,
    tagIconStyle
} from './index.style'
import DisplayPrice from '../display-price'
import {filterImageGroups, getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import Link from '@salesforce/retail-react-app/app/components/link'
import {pxToRem} from '../../utills/pxToRem'
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import CloudinaryPlpImage from '../../cloudinary/cloudinary-plp-images'
import {variantValues} from '../item-variant/index.style'
import useCustomFetch from '../../hooks/useStoreDetails'
import {useAccessToken, useProduct} from '@salesforce/commerce-sdk-react'
import {useGlobalError} from '../../pages/cart/util/global-error-context'
import PromotionsSection from '../promotions-section'

const QuantityPickerSection = ({
    quantity,
    setSelectedQuantity,
    minOrderQuantity = 1,
    stockLevel,
    setQuantity,
    stepQuantity = 1,
    isVisible = true,
    onQuantityChange,
    product,
    onCheckStock,
    disableIncrease = false
}) => {
    const maxAllowed = Math.min(stockLevel, 10)
    const [isLoading, setIsLoading] = useState(false)

    const dec = async () => {
        try {
            setIsLoading(true)

            let currentStockLevel = stockLevel

            if (onCheckStock && product) {
                currentStockLevel = await onCheckStock(product)
            }

            if (quantity > currentStockLevel) {
                const adjustedQuantity = Math.max(currentStockLevel, minOrderQuantity)
                setQuantity(adjustedQuantity)
                if (onQuantityChange) {
                    onQuantityChange(adjustedQuantity)
                }
            } else {
                const newQuantity =
                    quantity - stepQuantity >= minOrderQuantity ? quantity - stepQuantity : quantity
                setQuantity(newQuantity)
                if (onQuantityChange) {
                    onQuantityChange(newQuantity)
                }
            }
        } catch (error) {
            console.error('Error checking stock level:', error)
            const newQuantity =
                quantity - stepQuantity >= minOrderQuantity ? quantity - stepQuantity : quantity
            setQuantity(newQuantity)
            if (onQuantityChange) {
                onQuantityChange(newQuantity)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const inc = () => {
        const newQuantity =
            quantity + stepQuantity <= maxAllowed ? quantity + stepQuantity : quantity
        setQuantity(newQuantity)
        if (onQuantityChange) {
            onQuantityChange(newQuantity)
        }
    }

    const plusDisabled = quantity + stepQuantity > maxAllowed || disableIncrease
    const minusDisabled = quantity - stepQuantity < minOrderQuantity

    if (setSelectedQuantity) {
        setSelectedQuantity(quantity)
    }

    return (
        isVisible && (
            <Flex sx={containerStyle}>
                <Button
                    sx={minusButtonStyle}
                    onClick={dec}
                    isDisabled={minusDisabled || isLoading}
                    isLoading={isLoading}
                >
                    <MinusIcon sx={iconStyle} />
                </Button>
                <Text sx={inputStyle} as="span">
                    {quantity}
                </Text>
                <Button sx={plusButtonStyle} onClick={inc} isDisabled={plusDisabled}>
                    <AddIcon sx={iconStyle} />
                </Button>
            </Flex>
        )
    )
}

QuantityPickerSection.propTypes = {
    quantity: PropTypes.number.isRequired,
    minOrderQuantity: PropTypes.number,
    stockLevel: PropTypes.number,
    setQuantity: PropTypes.func.isRequired,
    stepQuantity: PropTypes.number,
    isVisible: PropTypes.bool,
    setSelectedQuantity: PropTypes.func,
    onQuantityChange: PropTypes.func,
    product: PropTypes.object,
    onCheckStock: PropTypes.func,
    disableIncrease: PropTypes.bool
}

const getInventoryErrorType = (quantity, stockLevel) => {
    if (stockLevel <= 0) {
        return 'OUT_OF_STOCK'
    }
    if (quantity > stockLevel) {
        return 'QUANTITY_EXCEEDED'
    }
    return null
}

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    primaryAction,
    secondaryActions,
    onItemQuantityChange = noop,
    showLoading = false,
    customStyles = {},
    isCart = true,
    isAccountPage = false,
    isOrderConfirmation = false,
    disableQuantityIncrease = false,
    isMixedCart = false,
    mixedCartProductMessage = ''
}) => {
    const isProductPartOfSet = !!product?.type?.set || false
    const isProductPartOfBundle = !!product?.type?.bundle || false
    const globalErrorContext = useGlobalError()
    const addError = globalErrorContext?.addError || (() => {})
    const removeError = globalErrorContext?.removeError || (() => {})

    const {data: tempProd} = useProduct({
        parameters: {
            id: product?.originalProductId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'bundled_products'
            ],
            allImages: true,
            c_isPDPRequest: true,
            c_isPickUpInStore: product?.showInStorePickup,
            inventoryIds: product?.c_storeId ? `inventory_store_${product?.c_storeId}` : null
        }
    })

    let {stepQuantity, showInventoryMessage, inventoryMessage, stockLevel, setQuantity} =
        useDerivedProduct(tempProd, isProductPartOfSet, isProductPartOfBundle)

    const {quantity} = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

    const [latestStockLevel, setLatestStockLevel] = useState(() => {
        if (tempProd?.inventories && tempProd.inventories.length > 0) {
            return tempProd.inventories[0].stockLevel || 0
        } else if (tempProd?.inventory) {
            return tempProd.inventory.stockLevel || 0
        } else if (stockLevel !== undefined) {
            return stockLevel
        }
        return 0
    })

    useEffect(() => {
        if (tempProd?.inventories && tempProd.inventories.length > 0) {
            const newStockLevel = tempProd.inventories[0].stockLevel || 0
            setLatestStockLevel(newStockLevel)
        } else if (tempProd?.inventory) {
            const newStockLevel = tempProd.inventory.stockLevel || 0
            setLatestStockLevel(newStockLevel)
        } else if (tempProd?.c_availableCount) {
            const newStockLevel = Number(tempProd.c_availableCount) || 0
            setLatestStockLevel(newStockLevel)
        } else if (stockLevel !== undefined) {
            setLatestStockLevel(stockLevel)
        }
    }, [tempProd, stockLevel])

    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()

    const {app: appConfig} = getConfig()
    const priceTotalThreshold = appConfig?.cart?.priceTotalThreshold || 200
    const pricePerUnitThreshold = appConfig?.cart?.pricePerUnitThreshold || 200

    const isGiftCertificate = product?.isGiftCertificate
    const [stockDataLoaded, setStockDataLoaded] = useState(false)

    useEffect(() => {
        if (
            tempProd &&
            (tempProd.inventories ||
                tempProd.inventory ||
                tempProd.c_availableCount ||
                stockLevel !== undefined)
        ) {
            setStockDataLoaded(true)
        }
    }, [tempProd, stockLevel])

    const inventoryErrorType = stockDataLoaded
        ? getInventoryErrorType(quantity || 0, latestStockLevel || 0)
        : null

    const handleCustomQuantityChange = (newQuantity) => {
        if (newQuantity >= 0) {
            onItemQuantityChange(newQuantity).then((isValidChange) => {
                if (isValidChange) {
                    setQuantity(newQuantity)
                }
            })
        }
    }

    const {data: freshProductData, refetch: refetchProduct} = useProduct(
        {
            parameters: {
                id: product?.originalProductId,
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'options',
                    'images',
                    'prices',
                    'variations',
                    'set_products',
                    'bundled_products'
                ],
                allImages: true,
                c_isPDPRequest: true,
                c_isPickUpInStore: product?.showInStorePickup,
                inventoryIds: product?.c_storeId ? `inventory_store_${product?.c_storeId}` : null
            }
        },
        {
            enabled: false
        }
    )

    const handleCheckStock = async (productToCheck) => {
        try {
            const result = await refetchProduct()

            if (result.data) {
                let currentStock = latestStockLevel || stockLevel || 0

                if (result.data.inventories && result.data.inventories.length > 0) {
                    currentStock = result.data.inventories[0].stockLevel || 0
                } else if (result.data.inventory) {
                    currentStock = result.data.inventory.stockLevel || 0
                } else if (result.data.c_availableCount) {
                    currentStock = Number(result.data.c_availableCount) || 0
                }

                setLatestStockLevel(currentStock)
                return currentStock
            } else {
                return latestStockLevel || 0
            }
        } catch (error) {
            console.error('Error fetching stock level:', error)
            return latestStockLevel || stockLevel || 0
        }
    }

    // Get price data with quantity=1 to ensure we get per-unit prices
    // DisplayPrice will handle quantity multiplication if needed
    const priceData = product && getPriceData(product, {quantity: 1})

    // Get prices from basket item (same approach as ItemPrice component used in minicart)
    // This ensures consistency with minicart behavior
    const basketItem = product?.basketItem

    // Basket item prices - need to check if they're per-unit or totals
    // SFCC basket API: price and priceAfterItemDiscount are typically PER-UNIT
    // But we need to ensure we always use per-unit prices
    let basketOriginalPrice = basketItem?.price != null ? parseFloat(basketItem.price) : null
    let basketDiscountedPrice =
        basketItem?.priceAfterItemDiscount != null
            ? parseFloat(basketItem.priceAfterItemDiscount)
            : basketOriginalPrice

    // If prices seem too high (likely totals), divide by quantity to get per-unit
    // Check if basketDiscountedPrice is close to expected total (quantity * expected per-unit)
    // If basketDiscountedPrice > 200 and quantity > 1, it might be a total
    if (
        basketDiscountedPrice != null &&
        quantity > 1 &&
        basketDiscountedPrice > 200 &&
        basketDiscountedPrice / quantity < 250
    ) {
        // Likely a total price, convert to per-unit
        basketDiscountedPrice = basketDiscountedPrice / quantity
        if (basketOriginalPrice != null && basketOriginalPrice > 200) {
            basketOriginalPrice = basketOriginalPrice / quantity
        }
    }

    // Also check transformBasket values as fallback
    const transformedBasePrice =
        product?.basePrice != null && product.basePrice !== undefined
            ? Number(product.basePrice)
            : null
    const transformedFinalPrice =
        product?.finalPrice != null && product.finalPrice !== undefined
            ? Number(product.finalPrice)
            : null

    // Prioritize transformedFinalPrice from transformBasket (already per-unit and validated)
    // Fallback to basket item prices (may need conversion), then catalog prices
    const finalPrice =
        transformedFinalPrice != null && !isNaN(transformedFinalPrice)
            ? transformedFinalPrice
            : basketDiscountedPrice != null && !isNaN(basketDiscountedPrice)
            ? basketDiscountedPrice
            : null

    // For basePrice, ALWAYS prioritize transformedBasePrice from transformBasketData
    // This is the preserved original price that should be used for all quantities
    // The transformBasketData function preserves basePrice from when it was first calculated
    // CRITICAL: transformedBasePrice should always be the preserved per-unit original price
    let basePrice = transformedBasePrice

    // If transformedBasePrice is invalid (0, null, or NaN), try other sources
    // But transformedBasePrice should always be valid if preservation is working
    // IMPORTANT: Only use fallbacks if transformedBasePrice is truly missing
    // If it's 0, that might be a real price, so we should still try to use it if finalPrice exists
    if (basePrice == null || isNaN(basePrice)) {
        // Only use basket price as fallback if transformedBasePrice is not available
        // AND basket price is higher than discounted price (indicating it might be original)
        if (
            basketOriginalPrice != null &&
            !isNaN(basketOriginalPrice) &&
            basketOriginalPrice > 0 &&
            finalPrice != null &&
            basketOriginalPrice > finalPrice
        ) {
            basePrice = basketOriginalPrice
        }
        // Fallback to catalog listPrice if we still don't have basePrice
        else if (priceData?.listPrice != null) {
            basePrice = priceData.listPrice
        }
    }
    // If basePrice is 0 or <= 0, but we have a finalPrice, we might still want to show it
    // But typically, if basePrice <= 0, there's no discount to show
    else if (basePrice <= 0 && finalPrice != null) {
        // If we have a finalPrice but basePrice is 0, try to get original from catalog
        if (priceData?.listPrice != null && priceData.listPrice > finalPrice) {
            basePrice = priceData.listPrice
        }
    }

    // Check if there's a discount: basePrice > finalPrice
    // Both must be valid numbers greater than 0
    const hasDiscount =
        basePrice != null &&
        finalPrice != null &&
        !isNaN(basePrice) &&
        !isNaN(finalPrice) &&
        basePrice > 0 &&
        basePrice > finalPrice

    // Build priceData - always use per-unit prices
    // DisplayPrice will multiply by quantity to show totals
    // Ensure we always have per-unit prices, never totals
    let newPriceData

    // Always prioritize basket item prices (per-unit) if available
    // Ensure we're using per-unit prices, not totals
    if (basePrice != null && finalPrice != null && !isNaN(basePrice) && !isNaN(finalPrice)) {
        // Double-check: if prices seem like totals (too high), divide by quantity
        // This is a safeguard in case basket API returns totals instead of per-unit
        let perUnitFinalPrice = finalPrice
        let perUnitBasePrice = basePrice

        // CRITICAL: Detect if finalPrice is a total (not per-unit) when quantity > 1
        // The basket API sometimes returns totals instead of per-unit prices
        // We need to convert totals to per-unit for correct discount calculation
        if (quantity > 1) {
            const expectedPerUnitFinalPrice = finalPrice / quantity

            // If we have a basePrice, use it to validate if finalPrice is a total
            // Key insight: if finalPrice is already per-unit, it should be <= basePrice
            // If finalPrice is a total, dividing by quantity should give us a per-unit price <= basePrice
            // But we need to be careful: if finalPrice is already per-unit and close to basePrice, don't divide
            if (basePrice > 0) {
                // Only divide if:
                // 1. finalPrice is significantly larger than basePrice (likely a total)
                // 2. OR dividing gives a reasonable per-unit price that's <= basePrice
                // 3. AND the divided price makes sense (not too small)
                const isLikelyTotal = finalPrice > basePrice // per-unit price can never exceed the list price

                if (isLikelyTotal) {
                    perUnitFinalPrice = expectedPerUnitFinalPrice
                }
                // If finalPrice is already <= basePrice, it's likely already per-unit, don't divide
            } else {
                // Uses configurable thresholds from environment variables (default: 200)
                if (
                    finalPrice > priceTotalThreshold &&
                    finalPrice / quantity < pricePerUnitThreshold
                ) {
                    perUnitFinalPrice = finalPrice / quantity
                }
            }

            // basePrice is always per-unit (catalog list price from transformBasketData/getPriceData),
        }

        // CRITICAL: Always show basePrice if it exists and is greater than finalPrice
        // This ensures the original price is always displayed when there's a discount
        // Even if the discount calculation is slightly off, we should show the original price
        const shouldShowListPrice =
            perUnitBasePrice != null &&
            perUnitFinalPrice != null &&
            !isNaN(perUnitBasePrice) &&
            !isNaN(perUnitFinalPrice) &&
            perUnitBasePrice > 0 &&
            perUnitBasePrice > perUnitFinalPrice

        // We have valid per-unit prices - use them directly
        newPriceData = {
            currentPrice: perUnitFinalPrice, // per-unit discounted price
            // Show crossed-out price if original > discounted (ALWAYS show if valid)
            listPrice: shouldShowListPrice ? perUnitBasePrice : null, // per-unit original price
            isOnSale: shouldShowListPrice,
            isASet: product?.type?.set || false,
            isMaster: product?.type?.master || false,
            isRange: false
        }
    } else {
        // Fallback to catalog prices (also per-unit)
        // IMPORTANT: priceData?.currentPrice from getPriceData might already be multiplied
        // We called getPriceData with quantity: 1, so it should be per-unit
        // But if it's still wrong, we need to ensure it's per-unit
        let fallbackCurrentPrice = priceData?.currentPrice
        // If fallback price seems like a total (too high), divide by quantity
        if (
            fallbackCurrentPrice != null &&
            quantity > 1 &&
            fallbackCurrentPrice > 200 &&
            fallbackCurrentPrice / quantity < 250
        ) {
            fallbackCurrentPrice = fallbackCurrentPrice / quantity
        }

        newPriceData = {
            ...priceData,
            // Use basket prices if available (per-unit), otherwise fall back to product catalog prices
            currentPrice:
                finalPrice != null && !isNaN(finalPrice) ? finalPrice : fallbackCurrentPrice,
            // Only set listPrice if there's a discount (basePrice > finalPrice)
            listPrice:
                hasDiscount && basePrice != null && !isNaN(basePrice) && basePrice > 0
                    ? basePrice
                    : basePrice != null && !isNaN(basePrice) && basePrice > 0
                    ? basePrice
                    : priceData?.listPrice,
            // isOnSale must be true for DisplayPrice to render listPrice
            isOnSale:
                hasDiscount ||
                (basePrice != null &&
                    finalPrice != null &&
                    !isNaN(basePrice) &&
                    !isNaN(finalPrice) &&
                    basePrice > finalPrice) ||
                priceData?.isOnSale ||
                false
        }
    }

    const isMasterVariant = !!product?.variants
    const selectableAttributeValue =
        isMasterVariant && !!product?.representedProduct
            ? product?.variants?.find(
                  (variant) => variant.productId == product.representedProduct.id
              )?.variationValues?.[selectableAttributeId]
            : undefined
    const selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
    const imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE

    const image = useMemo(() => {
        const hasSelectableAttribute = product?.variationAttributes?.find(
            ({id}) => id === selectableAttributeId
        )

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}
        const filteredImageGroups = filterImageGroups(product?.imageGroups || [], {
            viewType: imageViewType,
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        return filteredImageGroups?.[0]?.images[0]
    }, [product, selectableAttributeId, selectableAttributeValue, imageViewType])

    const {token} = useAccessToken()
    const {response} = useCustomFetch(token, product?.c_storeId)
    const shippingState = product?.shippingState

    const shouldShowError =
        !showLoading && tempProd && (product?.isRestricted || inventoryErrorType)

    useEffect(() => {
        if (shouldShowError && !isGiftCertificate && inventoryErrorType) {
            addError(product.itemId)
        } else {
            removeError(product.itemId)
        }

        return () => {
            removeError(product.itemId)
        }
    }, [shouldShowError, inventoryErrorType, isGiftCertificate, product.itemId])

    const transformProductUrl = (urlPath) => {
        if (!urlPath) {
            return ''
        }
        const url = new URL(urlPath).pathname
        const segments = url.split('/')
        const filename = segments.pop().replace('.html', '')
        const parts = filename.split('_')
        const colorCode = parts.slice(1).find((part) => /^\d+$/.test(part)) || ''
        const newPath = `${segments.join('/')}/${parts[0]}.html`
        return colorCode ? `${newPath}?color=${colorCode}` : newPath
    }

    const transformedUrl = transformProductUrl(product?.slugUrl || tempProd?.slugUrl)

    return (
        <>
            <Box className="inner-container" display={{base: 'none', md: 'block'}}>
                <Box
                    position="relative"
                    height="fit-content"
                    display="flex"
                    flexDirection="column"
                    data-testid={`sf-cart-item-${
                        product.productId ? product.productId : product.id
                    }`}
                >
                    <ItemVariantProvider variant={product}>
                        {showLoading && <LoadingSpinner />}
                        <Flex
                            width="full"
                            alignItems="stretch"
                            backgroundColor="white"
                            minHeight="200px"
                            height="fit-content"
                        >
                            {isGiftCertificate ? (
                                <Box
                                    className="image-container"
                                    sx={{
                                        ...(customStyles.imageStyle || imageStyle),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img
                                        src={product?.imageUrl}
                                        alt="Gift Certificate"
                                        title={product?.name}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    className="image-container"
                                    sx={customStyles.imageStyle || imageStyle}
                                >
                                    <CloudinaryPlpImage
                                        cloudinaryImage={
                                            product?.c_cloudinary ? product.c_cloudinary : null
                                        }
                                        image={image}
                                        title={product?.name}
                                    />
                                </Box>
                            )}
                            <Stack
                                flex={1}
                                spacing={0}
                                sx={{
                                    m: `${pxToRem(8)} ${pxToRem(10)} ${pxToRem(8)} ${pxToRem(15)}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box sx={{flex: '1 1 auto'}}>
                                    <Box sx={{mb: pxToRem(14), lineHeight: 'normal'}}>
                                        {isGiftCertificate ? (
                                            <Text sx={productNameStyle}>{product.name}</Text>
                                        ) : (
                                            <Link to={transformedUrl} sx={productNameStyle}>
                                                {product.name}
                                            </Link>
                                        )}
                                        {product?.c_freightSurcharge && (
                                            <Text sx={surchargeStyle}>
                                                {product.c_freightSurcharge}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        {isGiftCertificate ? (
                                            <Stack spacing="10px">
                                                {product.price && (
                                                    <Box>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_amount',
                                                                    defaultMessage:
                                                                        'Amount: ${price}'
                                                                },
                                                                {price: product?.price}
                                                            )}
                                                        </Text>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_to',
                                                                    defaultMessage:
                                                                        'To: {recipientName}'
                                                                },
                                                                {
                                                                    recipientName:
                                                                        product?.recipientName
                                                                }
                                                            )}
                                                        </Text>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_from',
                                                                    defaultMessage:
                                                                        'From: {senderName}'
                                                                },
                                                                {
                                                                    senderName: product?.senderName
                                                                }
                                                            )}
                                                        </Text>
                                                    </Box>
                                                )}
                                                <Text as="span" sx={shipToHomeStyle}>
                                                    {intl.formatMessage(
                                                        {
                                                            id: 'cart_email_delivery',
                                                            defaultMessage:
                                                                'Email Delivery: {recipientEmail}'
                                                        },
                                                        {
                                                            recipientEmail: product?.recipientEmail
                                                        }
                                                    )}
                                                </Text>
                                            </Stack>
                                        ) : (
                                            <CartItemVariantAttributes
                                                product={product}
                                                includeQuantity={!isCart}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{mb: pxToRem(16)}}>
                                        {product?.c_storeId && product?.showInStorePickup ? (
                                            <Text
                                                as="span"
                                                sx={{...shipToHomeStyle, mb: pxToRem(9)}}
                                            >
                                                {isMixedCart && mixedCartProductMessage !== ''
                                                    ? mixedCartProductMessage
                                                    : intl.formatMessage({
                                                          id: 'product_list.store.pickupinstore',
                                                          defaultMessage: 'Pick Up in Store'
                                                      })}
                                            </Text>
                                        ) : (
                                            product?.showShipToHome && (
                                                <Text sx={{...shipToHomeStyle, mb: pxToRem(13)}}>
                                                    {intl.formatMessage({
                                                        id: 'product_list.store.shiptome',
                                                        defaultMessage: 'Ship to Me'
                                                    })}
                                                </Text>
                                            )
                                        )}
                                        {product?.c_storeId && product?.showInStorePickup && (
                                            <Text sx={{...shipToHomeStyle, mb: pxToRem(9)}}>
                                                {`${response?.data?.[0].address1} ${response?.data?.[0].city}, ${response?.data?.[0].stateCode} ${response?.data?.[0].postalCode}`}
                                            </Text>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    sx={{flex: '0 0 auto'}}
                                    width={isAccountPage ? '100%' : 'auto'}
                                >
                                    <Flex
                                        direction="row"
                                        align="end"
                                        justify="space-between"
                                        width={isAccountPage ? '100%' : 'auto'}
                                    >
                                        <Flex
                                            direction="column"
                                            gap={0}
                                            width={isAccountPage ? '100%' : 'auto'}
                                        >
                                            <Box
                                                sx={{
                                                    maxW: {
                                                        xl: pxToRem(300)
                                                    },
                                                    mb: pxToRem(10),
                                                    display: 'block'
                                                }}
                                            >
                                                <DisplayPrice
                                                    currency={activeCurrency}
                                                    priceData={newPriceData}
                                                    quantity={quantity}
                                                    currentPriceProps={
                                                        newPriceData.isOnSale
                                                            ? {...priceBaseStyle, ...priceStyle}
                                                            : {...priceBaseStyle}
                                                    }
                                                    listPriceProps={priceBaseStyle}
                                                />

                                                <PromotionsSection
                                                    promos={product?.productPromotions}
                                                />
                                            </Box>
                                            <Flex
                                                align="end"
                                                justify="space-between"
                                                flexWrap="no-wrap"
                                            >
                                                <Box>{secondaryActions}</Box>
                                                {isAccountPage && <Box>{primaryAction}</Box>}
                                            </Flex>
                                        </Flex>
                                        {(latestStockLevel || 0) > 0 && isCart && (
                                            <Box
                                                sx={{
                                                    alignSelf: 'flex-end',
                                                    mr: pxToRem(-10),
                                                    mb: pxToRem(2)
                                                }}
                                            >
                                                <QuantityPickerSection
                                                    quantity={quantity}
                                                    setQuantity={setQuantity}
                                                    stepQuantity={stepQuantity}
                                                    stockLevel={latestStockLevel}
                                                    minOrderQuantity={1}
                                                    isVisible={true}
                                                    onQuantityChange={handleCustomQuantityChange}
                                                    product={product}
                                                    onCheckStock={handleCheckStock}
                                                    disableIncrease={disableQuantityIncrease}
                                                />
                                            </Box>
                                        )}
                                    </Flex>
                                </Box>
                            </Stack>
                        </Flex>
                        {!isGiftCertificate && shouldShowError && !isOrderConfirmation && (
                            <Stack spacing={1.5} alignItems="flex-start" sx={{mt: pxToRem(4)}}>
                                {product?.isRestricted && !product?.showInStorePickup && (
                                    <Flex flexDirection="row" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'cart_error.shipment_change',
                                                    defaultMessage: `Item can't be shipped to {shippingState} please remove or change shipping address.`
                                                },
                                                {shippingState}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'OUT_OF_STOCK' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage({
                                                id: 'cart_error.out_of_stock',
                                                defaultMessage:
                                                    'This item is no longer available. Please remove this from the cart.'
                                            })}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'QUANTITY_EXCEEDED' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'cart_error.quantity_exceeded',
                                                    defaultMessage:
                                                        'The quantity selected is not currently available, please enter a quantity of {stockLevel} or less to proceed with this purchase.'
                                                },
                                                {stockLevel: latestStockLevel}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                            </Stack>
                        )}
                    </ItemVariantProvider>
                </Box>
            </Box>
            <Box display={{base: 'block', md: 'none'}} width={'100%'}>
                <Box
                    position="relative"
                    width="100%"
                    height="100%"
                    data-testid={`sf-cart-item-${
                        product.productId ? product.productId : product.id
                    }`}
                >
                    <ItemVariantProvider variant={product}>
                        {showLoading && <LoadingSpinner />}
                        <Flex direction="row" alignItems="flex-start">
                            {isGiftCertificate ? (
                                <img
                                    src={product.imageUrl}
                                    alt="Gift Certificate"
                                    title={product.name}
                                    style={{width: '127px', height: 'auto'}}
                                />
                            ) : (
                                <Box sx={customStyles.imageStyle || imageStyle}>
                                    <CloudinaryPlpImage
                                        cloudinaryImage={
                                            product?.c_cloudinary ? product.c_cloudinary : null
                                        }
                                        image={image}
                                        title={product.name}
                                    />
                                </Box>
                            )}
                            <Stack
                                flex={1}
                                spacing="0"
                                sx={{
                                    m: `${pxToRem(2)} ${pxToRem(0)} ${pxToRem(8)} ${pxToRem(10)}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: '127px',
                                    height: '100%'
                                }}
                            >
                                <Box
                                    sx={{
                                        mb: {base: pxToRem(2), md: pxToRem(5)},
                                        lineHeight: 'normal'
                                    }}
                                >
                                    {isGiftCertificate ? (
                                        <Text sx={productNameStyle}>{product.name}</Text>
                                    ) : (
                                        <Link to={transformedUrl} sx={productNameStyle}>
                                            {product.name}
                                        </Link>
                                    )}
                                    {product?.c_freightSurcharge && (
                                        <Text sx={surchargeStyle}>
                                            {product.c_freightSurcharge}
                                        </Text>
                                    )}
                                </Box>
                                <Box sx={{mb: pxToRem(5)}}>
                                    {isGiftCertificate ? (
                                        <Stack spacing="5px">
                                            {product.price && (
                                                <Box>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_amount',
                                                                defaultMessage: 'Amount: ${price}'
                                                            },
                                                            {price: product.price}
                                                        )}
                                                    </Text>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_to',
                                                                defaultMessage:
                                                                    'To: {recipientName}'
                                                            },
                                                            {
                                                                recipientName: product.recipientName
                                                            }
                                                        )}
                                                    </Text>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_from',
                                                                defaultMessage: 'From: {senderName}'
                                                            },
                                                            {senderName: product.senderName}
                                                        )}
                                                    </Text>
                                                </Box>
                                            )}
                                            <Text as="span" sx={shipToHomeStyle}>
                                                {intl.formatMessage(
                                                    {
                                                        id: 'cart_email_delivery',
                                                        defaultMessage:
                                                            'Email Delivery: {recipientEmail}'
                                                    },
                                                    {recipientEmail: product?.recipientEmail}
                                                )}
                                            </Text>
                                        </Stack>
                                    ) : (
                                        <CartItemVariantAttributes
                                            product={product}
                                            includeQuantity={!isCart}
                                            currency={product?.currency}
                                        />
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        mx: 0,
                                        my: 0,
                                        px: 0,
                                        py: 0,
                                        mb: pxToRem(5)
                                    }}
                                >
                                    {product?.showInStorePickup ? (
                                        <Text as="span" sx={shipToHomeStyle}>
                                            {intl.formatMessage({
                                                id: 'product_list.store.pickupinstore',
                                                defaultMessage: 'Pick Up in Store'
                                            })}
                                        </Text>
                                    ) : (
                                        product?.showShipToHome && (
                                            <Text as="span" sx={shipToHomeStyle}>
                                                {intl.formatMessage({
                                                    id: 'product_list.store.shiptome',
                                                    defaultMessage: 'Ship to Me'
                                                })}
                                            </Text>
                                        )
                                    )}
                                    {product?.c_storeId && product?.showInStorePickup && (
                                        <>
                                            <Text sx={{...shipToHomeStyle, m: 0}}>
                                                {response?.data?.[0].address1}
                                            </Text>
                                            <Text
                                                sx={{...shipToHomeStyle, m: 0}}
                                            >{`${response?.data?.[0].city}, ${response?.data?.[0].stateCode} ${response?.data?.[0].postalCode}`}</Text>
                                        </>
                                    )}
                                </Box>
                                <Box sx={{mb: pxToRem(5)}}>
                                    <DisplayPrice
                                        currency={activeCurrency}
                                        priceData={newPriceData}
                                        quantity={quantity}
                                        currentPriceProps={
                                            newPriceData.isOnSale
                                                ? {...priceBaseStyle, ...priceStyle}
                                                : {...priceBaseStyle}
                                        }
                                        listPriceProps={priceBaseStyle}
                                    />
                                </Box>
                            </Stack>
                        </Flex>
                        <Stack spacing="0" width={'100%'}>
                            <Box>
                                <PromotionsSection promos={product?.productPromotions} />
                            </Box>
                            <Flex
                                direction="row"
                                align="center"
                                justify="space-between"
                                flexWrap="wrap"
                            >
                                <Box>{secondaryActions}</Box>
                                {isAccountPage && <Box width="100%">{primaryAction}</Box>}
                                {(latestStockLevel || 0) > 0 && isCart && (
                                    <Box
                                        sx={{
                                            alignSelf: 'flex-end'
                                        }}
                                    >
                                        <QuantityPickerSection
                                            quantity={quantity}
                                            setQuantity={setQuantity}
                                            stepQuantity={stepQuantity}
                                            stockLevel={latestStockLevel}
                                            minOrderQuantity={1}
                                            isVisible={true}
                                            onQuantityChange={handleCustomQuantityChange}
                                            product={product}
                                            onCheckStock={handleCheckStock}
                                            disableIncrease={disableQuantityIncrease}
                                        />
                                    </Box>
                                )}
                            </Flex>
                        </Stack>
                        {!isGiftCertificate && shouldShowError && !isOrderConfirmation && (
                            <Stack mt="5px" spacing={1.5} alignItems="flex-start">
                                {product?.isRestricted && !product?.showInStorePickup && (
                                    <Flex flexDirection="row" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'product_item.error.shipment_change',
                                                    defaultMessage: `Item can't be shipped to {shippingState} please remove or change shipping address`
                                                },
                                                {shippingState}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'OUT_OF_STOCK' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage({
                                                id: 'product_item.error.out_of_stock',
                                                defaultMessage:
                                                    'This item is no longer available. Please remove this from the cart.'
                                            })}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'QUANTITY_EXCEEDED' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'product_item.error.quantity_exceeded',
                                                    defaultMessage:
                                                        'The quantity selected is not currently available, please enter a quantity of {stockLevel} or less to proceed with this purchase.'
                                                },
                                                {stockLevel: latestStockLevel}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                            </Stack>
                        )}
                    </ItemVariantProvider>
                </Box>
            </Box>
        </>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    customStyles: PropTypes.object,
    isCart: PropTypes.bool,
    isAccountPage: PropTypes.bool,
    isOrderConfirmation: PropTypes.bool,
    disableQuantityIncrease: PropTypes.bool,
    isMixedCart: PropTypes.bool,
    mixedCartProductMessage: PropTypes.string
}

export default ProductItem
